import type { User } from '@/api/user/userType';
import { createRouter, createWebHistory } from 'vue-router';
import { fetchUserApi } from '@/api/user/userApi';
import { clearDynamicCourseContext } from '@/components/course/courseUtils';
import { installLibraryEntryTracking } from '@/features/ai-visualization/v3/library-navigation';
import AuthPage from '@/pages/AuthPage.vue';
import MainPage from '@/pages/MainPage.vue';
import { useConfigStore, useUserStore } from '@/store';
import { isAnnouncementManagerUser, isPlatformAdminUser } from '@/utils/auth';

const LOCAL_TEACHER_PREVIEW_KEY = 'probfun_local_teacher_preview';
const LOCAL_ADMIN_PREVIEW_KEY = 'probfun_local_admin_preview';
const LOCAL_STUDENT_PREVIEW_KEY = 'probfun_local_student_preview';

const router = createRouter({
  history: createWebHistory(),
  routes: [
    {
      path: '/login',
      component: AuthPage,
    },
    {
      path: '/signup',
      component: AuthPage,
    },
    {
      path: '/home',
      component: () => import('@/pages/OnboardingPage.vue'),
    },
    {
      path: '/callback',
      component: () => import('@/components/auth/Callback.vue'),
    },
    {
      path: '/wechat/callback',
      component: () => import('@/components/auth/WechatCallback.vue'),
    },
    {
      path: '/sso/imathbook',
      component: () => import('@/components/auth/ImathbookSsoCallback.vue'),
    },
    {
      path: '/my-courses',
      component: () => import('@/components/course/MyCoursesPage.vue'),
    },
    {
      path: '/class-management',
      component: () => import('@/components/class/ClassManagementPage.vue'),
    },
    {
      path: '/courses/ai-create',
      component: () => import('@/components/course/CourseAICreatePage.vue'),
    },
    {
      path: '/courses/:courseId',
      component: () => import('@/components/course/CourseEntryPage.vue'),
    },
    {
      path: '/courses/:courseId/manage',
      component: () => import('@/components/course/CourseWorkspacePage.vue'),
    },
    {
      path: '/courses/:courseId/analytics',
      component: () => import('@/components/course/CourseClassAnalyticsPage.vue'),
    },
    {
      path: '/admin/course-reviews',
      component: () => import('@/components/course/CourseReviewAdminPage.vue'),
    },
    {
      path: '/admin/announcements',
      component: () => import('@/components/announcement/AnnouncementAdminPage.vue'),
    },
    {
      path: '/announcements',
      component: () => import('@/pages/AnnouncementsPage.vue'),
    },
    {
      path: '/notifications',
      component: () => import('@/components/notification/NotificationCenterPage.vue'),
    },
    {
      path: '/wechat/bind',
      component: () => import('@/pages/WechatBindPage.vue'),
    },
    {
      path: '/high-school',
      component: () => import('@/pages/HighSchoolMainPage.vue'),
    },
    {
      path: '/learning-tools',
      component: () => import('@/pages/LearningToolsPage.vue'),
    },
    {
      path: '/zhiwei/projects',
      name: 'AiVisualizationProjects',
      component: () => import('@/features/ai-visualization/v2/projects/VisualizationProjectsPage.vue'),
    },
    {
      path: '/zhiwei/projects/:projectId',
      name: 'AiVisualizationStudio',
      component: () => import('@/features/ai-visualization/v2/studio/VisualizationStudioPage.vue'),
    },
    {
      // 旧引导页已被"新建项目 → 引导模式"取代（知微 2.0 引导层在 studio 内）。
      // 保留重定向，老书签/旧链接不至于 404。
      path: '/zhiwei/guide',
      redirect: '/zhiwei/projects',
    },
    {
      path: '/ai-visualization/projects',
      redirect: '/zhiwei/projects',
    },
    {
      path: '/ai-visualization/studio',
      // 裸路径不再隐式新建项目：带项目身份（query.projectId）或 fixture 才进
      // 工作台，其余一律回项目列表——知微的入口是列表页。
      beforeEnter: to => (to.query.projectId || to.query.fixture ? true : { name: 'AiVisualizationProjects' }),
      component: () => import('@/features/ai-visualization/v2/studio/VisualizationStudioPage.vue'),
    },
    {
      path: '/ai-visualization/local-studio',
      name: 'AiVisualizationLocalStudio',
      component: () => import('@/features/ai-visualization/local/LocalWorkspaceStudioPage.vue'),
    },
    {
      path: '/ai-visualization/workbench',
      redirect: '/zhiwei/projects',
    },
    {
      path: '/ai-visualization/gallery',
      name: 'AiVisualizationGallery',
      component: () => import('@/features/ai-visualization/gallery/VisualizationGalleryPage.vue'),
    },
    {
      path: '/',
      component: MainPage,
      children: [
        {
          path: '',
          component: () => import('@/components/dashboard/DashBoard.vue'),
        },
        //   probability
        {
          path: '/experiment/buffon',
          component: () => import('@/components/experiment/chapter1/buffon-needle/BuffonNeedle.vue'),
        },
        {
          path: '/experiment/three-doors',
          component: () => import('@/components/experiment/chapter1/three-doors/ThreeDoors.vue'),
        },
        {
          path: '/experiment/positive-test',
          component: () => import('@/components/experiment/chapter1/PositiveTest.vue'),
        },
        {
          path: '/experiment/birthday-attack',
          component: () => import('@/components/experiment/chapter1/BirthdayAttack.vue'),
        },
        {
          path: '/experiment/birthday-problem',
          component: () => import('@/components/experiment/chapter1/birthday-problem/BirthdayProblem.vue'),
        },
        {
          path: '/experiment/normal-distribution',
          component: () => import('@/components/experiment/distribution/normal-distribution/NormalDistribution.vue'),
        },
        {
          path: '/experiment/event-relationship',
          component: () => import('@/components/experiment/chapter1/EventRelationship.vue'),
        },
        {
          path: '/experiment/discrete-pmf',
          component: () => import('@/components/experiment/chapter2/DiscretePMF.vue'),
        },
        {
          path: '/experiment/pdf-cdf',
          component: () => import('@/components/experiment/chapter2/PdfCdf.vue'),
        },
        {
          path: '/experiment/binomial-distribution',
          component: () => import('@/components/experiment/distribution/BinomialDistribution.vue'),
        },
        {
          path: '/experiment/poisson-distribution',
          component: () => import('@/components/experiment/distribution/poisson-distribution/PoissonDistribution.vue'),
        },
        {
          path: '/experiment/geometric-distribution',
          component: () => import('@/components/experiment/distribution/geometric-distribution/GeometricDistribution.vue'),
        },
        {
          path: '/experiment/uniform-distribution',
          component: () => import('@/components/experiment/distribution/UniformDistribution.vue'),
        },
        {
          path: '/experiment/exponential-distribution',
          component: () => import('@/components/experiment/distribution/ExponentialDistribution.vue'),
        },
        {
          path: '/experiment/binomial-poisson',
          component: () => import('@/components/experiment/distribution/comparison/BP.vue'),
        },
        {
          path: '/experiment/binomial-normal',
          component: () => import('@/components/experiment/distribution/comparison/BN.vue'),
        },
        {
          path: '/experiment/poisson-exponential',
          component: () => import('@/components/experiment/distribution/comparison/PE.vue'),
        },
        {
          path: '/experiment/poisson-normal',
          component: () => import('@/components/experiment/distribution/comparison/PN.vue'),
        },
        {
          path: '/experiment/2d-uniform-distribution',
          component: () => import('@/components/experiment/chapter3/uniform-distribution/threeUniformDistribution.vue'),
        },
        {
          path: '/experiment/2d-normal-distribution',
          component: () => import('@/components/experiment/chapter3/normal-distribution/threeNormalDistribution.vue'),
        },
        {
          path: '/experiment/central-limit-theorem',
          component: () => import('@/components/experiment/chapter5/central-limit-theorem/clt.vue'),
        },
        {
          path: '/experiment/expectation-test',
          component: () => import('@/components/experiment/chapter4/ExpectationTest.vue'),
        },
        {
          path: '/experiment/bertrand',
          component: () => import('@/components/experiment/chapter1/bertrand/bertrand.vue'),
        },
        {
          path: '/experiment/distribution-clt',
          component: () => import('@/components/experiment/chapter5/distribution-clt/DistributionClt.vue'),
        },
        // {
        //   path: '/experiment/coin-tossing',
        //   component: () => import('@/components/experiment/chapter7/CoinTossing.vue'),
        // },
        // {
        //   path: '/experiment/black-scholes',
        //   component: () => import('@/components/experiment/chapter7/BlackScholesModel.vue'),
        // },
        // {
        //   path: '/experiment/american-option',
        //   component: () => import('@/components/experiment/chapter7/PricingofAmericanPutOptions.vue'),
        // },
        // {
        //   path: '/experiment/asset-captial',
        //   component: () => import('@/components/experiment/chapter7/AssetCapitalPricing.vue'),
        // },
        // {
        //   path: '/experiment/monte-carlo',
        //   component: () => import('@/components/experiment/chapter7/MonteCarlo.vue'),
        // },
        // {
        //   path: '/experiment/arithmetic-mean',
        //   component: () => import('@/components/experiment/chapter7/ArithmeticMeanAsianOption.vue'),
        // },
        // {
        //   path: '/experiment/obstacle-options',
        //   component: () => import('@/components/experiment/chapter7/ObstacleOptions.vue'),
        // },
        {
          path: '/experiment/t-distribution',
          component: () => import('@/components/experiment/chapter7/new experiment/TDistribution.vue'),
        },
        {
          path: '/experiment/chi-square-distribution',
          component: () => import('@/components/experiment/chapter7/new experiment/ChiSquareDistribution.vue'),
        },
        {
          path: '/experiment/t-distribution-quantile',
          component: () => import('@/components/experiment/chapter7/TDistributionQuantile.vue'),
        },
        {
          path: '/experiment/chi-square-distribution-quantile',
          component: () => import('@/components/experiment/chapter7/ChiSquareDistributionQuantile.vue'),
        },
        {
          path: '/experiment/brownian-motion',
          component: () => import('@/components/experiment/chapter6/Brownian.vue'),
        },
        {
          path: '/experiment/geometric-brownian-motion',
          component: () => import('@/components/experiment/chapter6/GeometricBrownian.vue'),
        },
        {
          path: '/experiment/poisson-process',
          component: () => import('@/components/experiment/chapter6/PoissonProcess.vue'),
        },
        {
          path: '/experiment/group-detection',
          component: () => import('@/components/experiment/chapter4/GroupDetection.vue'),
        },
        {
          path: '/experiment/portfolio',
          component: () => import('@/components/experiment/chapter4/Portfolio.vue'),
        },
        {
          path: '/subject-links',
          component: () => import('@/pages/SubjectLinksPage.vue'),
        },
        {
          path: '/experiment/NetworkTrafficCharacteristics',
          component: () => import('@/components/experiment/chapter4/NetworkTrafficCharacteristics.vue'),
        },
        {
          path: '/experiment/CoinFliping',
          component: () => import('@/components/experiment/chapter5/CoinFliping.vue'),
        },
        {
          path: '/experiment/EstimationOfPai',
          component: () => import('@/components/experiment/chapter5/EstimationOfPai.vue'),
        },
        //   advanced-math-1
        {
          path: '/experiment/sequence-limit',
          component: () => import('@/components/experiment/calculusA/SequenceLimit.vue'),
        },
        {
          path: '/experiment/function-limit',
          component: () => import('@/components/experiment/calculusA/FunctionLimit.vue'),
        },
        {
          path: '/experiment/derivative-geometry',
          component: () => import('@/components/experiment/calculusA/DerivativeGeometry.vue'),
        },
        {
          path: '/experiment/taylor-expansion',
          component: () => import('@/components/experiment/calculusA/TaylorExpansion.vue'),
        },
        {
          path: '/experiment/integrability-conditions',
          component: () => import('@/components/experiment/calculusA/IntegrabilityConditions.vue'),
        },
        {
          path: '/experiment/lagrange-mvt',
          component: () => import('@/components/experiment/calculusA/LagrangeMVT.vue'),
        },
        {
          path: '/experiment/gabriel-horn',
          component: () => import('@/components/experiment/calculusA/GabrielHorn.vue'),
        },
        //   advanced-math-2
        {
          path: '/experiment/partial-derivative',
          component: () => import('@/components/experiment/calculusB/PartialDerivative.vue'),
        },
        {
          path: '/experiment/total-derivative',
          component: () => import('@/components/experiment/calculusB/TotalDerivative.vue'),
        },
        {
          path: '/experiment/neighborhood',
          component: () => import('@/components/experiment/calculusB/Neighborhood.vue'),
        },
        {
          path: '/experiment/linear-equation',
          component: () => import('@/components/experiment/linearAlgebra/LinearEquation.vue'),
        },
        {
          path: '/experiment/linear-equation-3',
          component: () => import('@/components/experiment/linearAlgebra/LinearEquation3.vue'),
        },
        {
          path: '/experiment/false-positive',
          component: () => import('@/components/experiment/chapter1/FalsePositive.vue'),
        },
        {
          path: '/experiment/mutually-exclusive-and-independent',
          component: () => import('@/components/experiment/chapter1/MutuallyExclusiveAndIndependent.vue'),
        },
        {
          path: '/experiment/spam-filtering',
          component: () => import('@/components/experiment/chapter1/SpamFiltering.vue'),
        },
        {
          path: '/experiment/height-stats',
          component: () => import('@/components/experiment/chapter2/HeightStats.vue'),
        },
        {
          path: '/experiment/double-integral',
          component: () => import('@/components/experiment/calculusB/DoubleIntegral.vue'),
        },
        {
          path: '/experiment/space-curve',
          component: () => import('@/components/experiment/calculusB/SpaceCurve.vue'),
        },
        {
          path: '/experiment/linear-combination',
          component: () => import('@/components/experiment/linearAlgebra/LinearCombination.vue'),
        },
        {
          path: '/experiment/matrix-eigen',
          component: () => import('@/components/experiment/linearAlgebra/MatrixEigen.vue'),
        },
        {
          path: '/experiment/discrete-distribution',
          component: () => import('@/components/experiment/chapter2/DiscreteDistribution.vue'),
        },
        {
          path: '/experiment/ball-battle',
          component: () => import('@/components/experiment/chapter6/ball-battle/BallBattle.vue'),
        },
        {
          path: '/experiment/quadratic-function',
          component: () => import('@/components/experiment/functions/QuadraticFunction.vue'),
        },
        {
          path: '/experiment/conic-section',
          component: () => import('@/components/experiment/analyticGeometry/ConicSection.vue'),
        },
        {
          path: '/experiment/derivative-basic',
          component: () => import('@/components/experiment/derivatives/DerivativeBasic.vue'),
        },
        {
          path: '/experiment/tilde',
          component: () => import('@/components/experiment/trigonometry/Tilde.vue'),
        },
        {
          path: '/experiment/put-pin',
          component: () => import('@/components/experiment/probabilityStatistics/PutPin.vue'),
        },
        {
          path: '/experiment/visualization/:visualizationId',
          component: () => import('@/components/experiment/custom/VisualizationResourceExperiment.vue'),
        },
        {
          path: '/experiment/infinite-recurrence1',
          component: () => import('@/components/experiment/sequencesSeries/InfiniteRecurrence1.vue'),
        },
        {
          path: '/experiment/ferris-wheel',
          component: () => import('@/components/experiment/trigonometry/FerrisWheel.vue'),
        },
        {
          path: '/experiment/spring-oscillator',
          component: () => import('@/components/experiment/trigonometry/SpringOscillator.vue'),
        },
        {
          path: '/experiment/simple-pendulum',
          component: () => import('@/components/experiment/trigonometry/SimplePendulum.vue'),
        },
        {
          path: '/experiment/trig-overlay',
          component: () => import('@/components/experiment/trigonometry/TrigOverlay.vue'),
        },
        {
          path: '/analysis',
          component: () => import('@/components/analysis/AnalysisPanel.vue'),
        },
        {
          path: '/ai',
          component: () => import('@/components/ai/AiPanel.vue'),
        },
        {
          path: '/character-flow',
          component: () => import('@/components/character-flow/CharacterFlowPanel.vue'),
        },
        {
          path: '/membership',
          component: () => import('@/pages/MembershipPage.vue'),
        },
        {
          path: '/mistakerecord',
          component: () => import('@/components/mistake-record/MistakeRecordPanel.vue'),
        },
        {
          path: '/share-resource',
          component: () => import('@/components/share-resource/ShareResourcePanel.vue'),
        },
        {
          path: '/share-resource/upload',
          component: () => import('@/components/share-resource/ShareResourceUpload.vue'),
        },
        {
          path: '/visualization',
          component: () => import('@/components/visualization/VisualizationPanel.vue'),
        },
        {
          path: '/week-thinking',
          component: () => import('@/components/week-thinking/WeekThinkingPanel.vue'),
        },
        {
          path: '/class-analysis',
          component: () => import('@/components/class/ClassAnalysisPanel.vue'),
        },
        {
          path: '/exam-analysis',
          component: () => import('@/components/exam-analysis/ExamAnalysisPage.vue'),
        },
        {
          path: '/mindmap/distribution',
          component: () => import('@/components/diagram/distribution-diagram/DistributionDiagram.vue'),
        },
        {
          path: '/mindmap/chapter-1',
          component: () => import('@/components/diagram/chapter-1/Chapter1Diagram.vue'),
        },
        {
          path: '/mindmap/chapter-4',
          component: () => import('@/components/diagram/chapter-4/Chapter4Diagram.vue'),
        },
        {
          path: '/question/:chapterId',
          name: 'DoQuestion',
          component: () => import('@/components/do-question/DoQuestionPanel.vue'),
        },
        {
          path: '/mindmap/question/:chapterId',
          name: 'QuestionDiagram',
          component: () => import('@/components/diagram/question-diagram/QuestionDiagram.vue'),
        },
      ],
    },

    {
      path: '/:pathMatch(.*)*',
      redirect: '/',
    },
  ],
});

let profileCheckedToken: string | null = null;

async function ensureStoredUserLoaded() {
  const userStore = useUserStore();
  const token = localStorage.getItem('token');
  if (!token)
    return userStore.user || null;
  if (profileCheckedToken === token && userStore.user)
    return userStore.user;

  try {
    const result = await fetchUserApi();
    userStore.user = result.user;
    profileCheckedToken = token;
    return result.user;
  }
  catch {
    return null;
  }
}

function buildLocalTeacherPreviewUser(): User {
  return {
    id: 'local-preview-teacher',
    username: 'teacher-preview',
    classId: '',
    realName: '本地预览教师',
    gender: '',
    email: '',
    phone: '',
    school: '本地预览',
    major: '数学',
    grade: 0,
    source: 0,
    avatarUrl: '',
    role: 2,
    isSuperuser: false,
    isPlatformAdmin: false,
  };
}

function buildLocalAdminPreviewUser(): User {
  return {
    id: 'local-preview-admin',
    username: 'admin-preview',
    classId: '',
    realName: '本地管理员',
    gender: '',
    email: '',
    phone: '',
    school: '本地预览',
    major: '数学',
    grade: 0,
    source: 0,
    avatarUrl: '',
    role: 9,
    isSuperuser: true,
    isPlatformAdmin: true,
  };
}

function buildLocalStudentPreviewUser(): User {
  return {
    id: 'local-preview-student',
    username: 'student-preview',
    classId: '',
    realName: '本地预览学生',
    gender: '',
    email: '',
    phone: '',
    school: '本地预览',
    major: '数学',
    grade: 2026,
    source: 0,
    avatarUrl: '',
    role: 1,
    isSuperuser: false,
    isPlatformAdmin: false,
  };
}

function ensureLocalTeacherPreview(to: { path: string; query: Record<string, any> }) {
  if (!import.meta.env.DEV || import.meta.env.MODE === 'local-acceptance')
    return;

  if (localStorage.getItem('token')) {
    sessionStorage.removeItem(LOCAL_TEACHER_PREVIEW_KEY);
    sessionStorage.removeItem(LOCAL_STUDENT_PREVIEW_KEY);
    return;
  }

  const isStudentPreviewRoute = to.query.preview === 'student' || sessionStorage.getItem(LOCAL_STUDENT_PREVIEW_KEY) === '1';
  const isAnonymousMyCoursesPreview = to.path === '/my-courses'
    && !localStorage.getItem('token')
    && !isStudentPreviewRoute;
  const shouldEnterTeacherPreview = [
    to.query.preview === 'teacher',
    typeof to.query.previewCourseId === 'string',
    isAnonymousMyCoursesPreview,
    to.path.startsWith('/courses/') && !isStudentPreviewRoute,
  ].some(Boolean);

  if (shouldEnterTeacherPreview) {
    sessionStorage.removeItem(LOCAL_ADMIN_PREVIEW_KEY);
    sessionStorage.removeItem(LOCAL_STUDENT_PREVIEW_KEY);
    sessionStorage.setItem(LOCAL_TEACHER_PREVIEW_KEY, '1');
  }

  if (sessionStorage.getItem(LOCAL_TEACHER_PREVIEW_KEY) !== '1')
    return;

  const userStore = useUserStore();
  if (!userStore.user || Number(userStore.user.role) !== 2)
    userStore.user = buildLocalTeacherPreviewUser();
}

function ensureLocalAdminPreview(to: { path: string; query: Record<string, any> }) {
  if (!import.meta.env.DEV)
    return;

  if (localStorage.getItem('token')) {
    sessionStorage.removeItem(LOCAL_ADMIN_PREVIEW_KEY);
    return;
  }

  if (to.query.preview === 'admin' || to.path === '/admin/course-reviews' || to.path === '/admin/announcements') {
    sessionStorage.removeItem(LOCAL_TEACHER_PREVIEW_KEY);
    sessionStorage.removeItem(LOCAL_STUDENT_PREVIEW_KEY);
    sessionStorage.setItem(LOCAL_ADMIN_PREVIEW_KEY, '1');
  }

  if (sessionStorage.getItem(LOCAL_ADMIN_PREVIEW_KEY) !== '1')
    return;

  const userStore = useUserStore();
  if (!isPlatformAdminUser(userStore.user))
    userStore.user = buildLocalAdminPreviewUser();
}

function ensureLocalStudentPreview(to: { path: string; query: Record<string, any> }) {
  if (!import.meta.env.DEV)
    return;

  if (to.query.preview === 'student') {
    sessionStorage.removeItem(LOCAL_TEACHER_PREVIEW_KEY);
    sessionStorage.removeItem(LOCAL_ADMIN_PREVIEW_KEY);
    sessionStorage.setItem(LOCAL_STUDENT_PREVIEW_KEY, '1');
  }

  if (sessionStorage.getItem(LOCAL_STUDENT_PREVIEW_KEY) !== '1')
    return;

  const userStore = useUserStore();
  if (!userStore.user || Number(userStore.user.role) !== 1)
    userStore.user = buildLocalStudentPreviewUser();
}

const dynamicCourseScopedPathPrefixes = [
  '/analysis',
  '/class-analysis',
  '/exam-analysis',
  '/experiment',
  '/mindmap',
  '/mistakerecord',
  '/question',
  '/share-resource',
  '/visualization',
  '/week-thinking',
];

function shouldKeepDynamicCourseContext(path: string) {
  return path === '/'
    || /^\/(?:zhiwei|ai-visualization)(?:\/|$)/.test(path)
    || dynamicCourseScopedPathPrefixes.some(prefix => path === prefix || path.startsWith(`${prefix}/`));
}

router.beforeEach(async (to, from, next) => {
  ensureLocalAdminPreview(to);
  ensureLocalStudentPreview(to);
  ensureLocalTeacherPreview(to);
  const token = localStorage.getItem('token');
  if (token && !['/login', '/signup', '/callback', '/wechat/callback'].includes(to.path)) {
    const user = await ensureStoredUserLoaded();
    if (!user) {
      next('/login');
      return;
    }
    if (user?.requiresProfileSetup && to.path !== '/') {
      next('/?setup=1');
      return;
    }
  }
  const configStore = useConfigStore();
  const isCourseEntryRoute = /^\/courses\/[^/]+$/.test(to.path);
  if (configStore.dynamicSubject && !shouldKeepDynamicCourseContext(to.path) && !isCourseEntryRoute) {
    configStore.dynamicSubject = null;
    clearDynamicCourseContext();
  }
  const isLocalCoursePreview = import.meta.env.DEV && to.path === '/' && typeof to.query.previewCourseId === 'string';
  const isLocalTeacherPreview = import.meta.env.DEV
    && import.meta.env.MODE !== 'local-acceptance'
    && sessionStorage.getItem(LOCAL_TEACHER_PREVIEW_KEY) === '1';
  const isLocalAdminPreview = import.meta.env.DEV && sessionStorage.getItem(LOCAL_ADMIN_PREVIEW_KEY) === '1';
  const isLocalStudentPreview = import.meta.env.DEV && sessionStorage.getItem(LOCAL_STUDENT_PREVIEW_KEY) === '1';
  if (isLocalTeacherPreview && ['/home', '/login', '/signup'].includes(to.path)) {
    next('/my-courses');
    return;
  }
  if (to.path === '/home' && token) {
    next('/');
    return;
  }
  if (to.path === '/') {
    if (token || isLocalCoursePreview || isLocalTeacherPreview || isLocalAdminPreview || isLocalStudentPreview) {
      await ensureStoredUserLoaded();
      next();
    }
    else {
      next('/home');
    }
    return;
  }
  else if (to.path === '/wechat/bind') {
    if (token) {
      next();
    }
    else {
      next('/login');
    }
    return;
  }

  if (token && !['/login', '/signup', '/callback', '/wechat/callback'].includes(to.path)) {
    await ensureStoredUserLoaded();
  }

  if (to.path === '/class-analysis') {
    const user = await ensureStoredUserLoaded();
    if (!user) {
      next('/login');
    }
    else if ((user.role ?? 0) <= 1) {
      next('/');
    }
    else {
      next();
    }
    return;
  }
  else if (to.path === '/my-courses') {
    const user = await ensureStoredUserLoaded();
    if (!user) {
      next('/login');
    }
    else if (![1, 2].includes(Number(user.role))) {
      next('/');
    }
    else {
      next();
    }
    return;
  }
  else if (to.path === '/class-management') {
    const user = await ensureStoredUserLoaded();
    if (!user) {
      next('/login');
    }
    else if ((user.role ?? 0) < 1) {
      next('/');
    }
    else {
      next();
    }
    return;
  }
  else if (to.path === '/courses/ai-create') {
    const user = await ensureStoredUserLoaded();
    if (!user) {
      next('/login');
    }
    else if ((user.role ?? 0) !== 2) {
      next('/my-courses');
    }
    else {
      next();
    }
    return;
  }
  else if (to.path === '/admin/course-reviews') {
    const user = await ensureStoredUserLoaded();
    if (!user) {
      next('/login');
    }
    else if (!isPlatformAdminUser(user)) {
      next('/');
    }
    else {
      next();
    }
    return;
  }
  else if (to.path === '/admin/announcements') {
    const user = await ensureStoredUserLoaded();
    const isLocalPreview = import.meta.env.DEV && (isLocalAdminPreview || isLocalTeacherPreview);
    if (!user && !isLocalPreview) {
      next('/login');
    }
    else if (!isAnnouncementManagerUser(user)) {
      next('/');
    }
    else {
      next();
    }
    return;
  }
  else if (to.path === '/announcements') {
    const user = await ensureStoredUserLoaded();
    const isLocalPreview = import.meta.env.DEV && (isLocalAdminPreview || isLocalTeacherPreview || isLocalStudentPreview);
    if (!user && !isLocalPreview) {
      next('/login');
    }
    else {
      next();
    }
    return;
  }
  else if (to.path === '/notifications') {
    const user = await ensureStoredUserLoaded();
    const isLocalPreview = import.meta.env.DEV
      && to.query.previewNotifications === '1'
      && Boolean(useUserStore().user);
    if (!user && !isLocalPreview) {
      next('/login');
    }
    else {
      next();
    }
    return;
  }
  else if (to.path.startsWith('/courses/') && to.path.endsWith('/manage')) {
    const user = await ensureStoredUserLoaded();
    if (!user) {
      next('/login');
    }
    else if ((user.role ?? 0) !== 2) {
      next('/my-courses');
    }
    else {
      next();
    }
    return;
  }
  else if (to.path.startsWith('/courses/') && to.path.endsWith('/analytics')) {
    const user = await ensureStoredUserLoaded();
    if (!user) {
      next('/login');
    }
    else if ((user.role ?? 0) !== 2) {
      next('/my-courses');
    }
    else {
      next();
    }
    return;
  }
  if (configStore.dynamicSubject && !shouldKeepDynamicCourseContext(to.path)) {
    configStore.dynamicSubject = null;
    clearDynamicCourseContext();
  }
  next();
});

installLibraryEntryTracking(router);
export default router;
